import React, { useState } from "react";
import { LedgerDetails } from "src/shared/services/interfacev2";
import { usePayment } from "../hooks/usePayment";
import ThermalPrinterTest from "src/print-configuration/PrintConfigration";
import { useUserTheme } from "src/shared/hooks/useUserTheme";
import CommonButtons from "src/shared/components/CommonButtons";

interface Props {
  customerData: LedgerDetails;
  customerId: string;
  refetchLedger: () => void;
}

const PaymentCard: React.FC<Props> = ({ customerData, customerId, refetchLedger }) => {
  const [amount, setAmount] = useState("");
  const [discount, setDiscount] = useState("");
  const [paymode, setPaymode] = useState<"Cash" | "Card">("Cash");

  // NOTE: update usePayment's handlePayment signature to accept discount separately,
  // e.g. handlePayment(amount, paymode, discount)
  const { handlePayment, paymentLoading, print } = usePayment(customerData, customerId, () => {}, refetchLedger);
  const { bgClass, fontClass } = useUserTheme();

  const balance = customerData.totalOutstandingBalance ?? 0;
  const isAdvance = balance < 0;
  const isZero = balance === 0;

  const maxAmount = Math.abs(balance);

  const numericAmount = Number(amount) || 0;
  const numericDiscount = Number(discount) || 0;

  // amount + discount together settle the outstanding balance — they are
  // independent fields, each only bounded by how much "room" is left
  // after the other one, NOT by each other's value.
  const totalSettled = numericAmount + numericDiscount;

  const validateAmount = (val: string, roomLeft: number) => {
    if (!/^\d*\.?\d{0,2}$/.test(val)) return null;
    if (!val) return "";
    const num = Number(val);
    if (num > roomLeft) {
      return roomLeft.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
    }
    if (num < 0) {
      return "0";
    }
    return val;
  };

  const handleAmountChange = (val: string) => {
    const value = validateAmount(val, maxAmount - numericDiscount);
    if (value !== null) {
      setAmount(value);
    }
  };

  const handleDiscountChange = (val: string) => {
    const value = validateAmount(val, maxAmount - numericAmount);
    if (value !== null) {
      setDiscount(value);
    }
  };

  const handleConfirm = () => {
    if (totalSettled <= 0) return;
    // amount = actual cash/card received, discount = amount waived off.
    // Both are sent through independently — discount is NOT subtracted
    // from amount here, since a discount-only settlement (amount = 0) is valid.
    handlePayment(numericAmount.toString(), paymode, numericDiscount.toString());
  };

  return (
    <div>
      <div className="w-auto rounded-xl bg-white border border-slate-400/90 shadow-inner p-3 flex flex-col gap-3">
        {/* Header */}
        <div className="flex  gap-2 items-center justify-between">
          <h2 className="text-base font-semibold text-slate-800 flex flex-1">{isAdvance ? "Refund" : "Payment"}</h2>
          <div className="flex bg-slate-100 rounded-lg p-1 gap-2 flex-1 justify-between">
            {["Cash", "Card"].map((mode) => (
              <div className="flex gap-2 cursor-pointer" onClick={() => setPaymode(mode as "Cash" | "Card")} key={mode}>
                <input type="radio" key={mode} checked={paymode === mode} />
                <p>{mode}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Amount + Discount inputs */}
        <div className="flex flex-row-reverse  gap-2 w-full">
          <div className="flex flex-col gap-1.5 flex-1">
            <label className="text-[11px] text-slate-500 uppercase tracking-wider">{isAdvance ? "Refund" : "Amount"}</label>

            <input
              type="number"
              placeholder="0.00"
              value={amount}
              disabled={isZero}
              onChange={(e) => handleAmountChange(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
            />
          </div>

          {!isAdvance && (
            <div className="flex flex-col gap-1.5 flex-1">
              <label className="text-[11px] text-slate-500 uppercase tracking-wider">Discount</label>

              <input
                type="number"
                placeholder="0.00"
                value={discount}
                disabled={isZero}
                onChange={(e) => handleDiscountChange(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
              />
            </div>
          )}
        </div>

        {/* Action */}
        {!isZero && <CommonButtons label={paymentLoading ? "..." : isAdvance ? "Refund" : "Pay"} className="w-full h-fit" disabled={paymentLoading || totalSettled <= 0} onClick={handleConfirm} />}

        {/* Empty state */}
        {isZero && <p className="text-xs text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">No dues — everything is settled ✅</p>}
      </div>

      {print && <ThermalPrinterTest ledgerPaid={print} openPrint={String(print)} />}
    </div>
  );
};

export default PaymentCard;
